import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CcdlButtonComponent,
  CcdlCheckboxComponent,
  CcdlInputComponent,
  CcdlLinkComponent,
  CcdlSingleSelectComponent,
  ErrorConfig,
  SingleOption,
} from '@cpm-pr1618001/ng-design-lib';
import { Countries, States } from '@cpm-pr1618003/ng-initializer-lib';
import { Subscription } from 'rxjs';

@Component({
  selector: 'sign-up-ui',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CcdlLinkComponent,
    CcdlButtonComponent,
    CcdlCheckboxComponent,
    CcdlInputComponent,
    CcdlSingleSelectComponent,
  ],
  standalone: true,
})
export class SignUpComponent implements OnInit, OnChanges, OnDestroy {
  @Input() signUpForm!: FormGroup;
  @Input() countries!: Countries[];
  @Input() states!: States[];
  @Input() isSubmitTriggered: boolean = false;

  @Output() signInViewEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() signUpSubmitEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() onTermsEmitter = new EventEmitter<void>();
  @Output() onPrivacyEmitter = new EventEmitter<void>();
  @Output() selectedCountry = new EventEmitter<string>();
  @Output() selectedState = new EventEmitter<string>();

  countryOptions: SingleOption[] = [];

  stateOptions: SingleOption[] = [];

  subscription: Subscription = new Subscription();

  firstNameErrorConfig: ErrorConfig = {
    required: 'First name is required.',
    pattern:
      'First name must be 2–50 characters (letters, spaces, or periods only).',
  };
  lastNameErrorConfig: ErrorConfig = {
    required: 'Last name is required.',
    pattern:
      'Last name must be 2–50 characters (letters, spaces, or periods only).',
  };
  emailErrorConfig: ErrorConfig = {
    required: 'Email address is required.',
    pattern: 'Please enter a valid email address.',
  };
  phoneErrorConfig: ErrorConfig = {
    required: 'Phone number is required.',
    pattern: 'Please enter a valid 10-digit phone number.',
  };
  passwordErrorConfig: ErrorConfig = {
    required: 'Password is required.',
    minlength: 'Password must be at least 8 characters.',
    uppercase: 'Must contain at least one uppercase letter.',
    lowercase: 'Must contain at least one lowercase letter.',
    digit: 'Must contain at least one number.',
    special: 'Must contain at least one special character (@$!%*#?&).',
  };
  confirmPasswordErrorConfig: ErrorConfig = {
    required: 'Please confirm your password.',
    minlength: 'Password must be at least 8 characters.',
    uppercase: 'Must contain at least one uppercase letter.',
    lowercase: 'Must contain at least one lowercase letter.',
    digit: 'Must contain at least one number.',
    special: 'Must contain at least one special character (@$!%*#?&).',
    mismatch: 'Passwords do not match.',
  };
  countryErrorConfig: ErrorConfig = { required: 'Please select a country.' };
  stateErrorConfig: ErrorConfig = { required: 'Please select a state.' };
  zipErrorConfig: ErrorConfig = {
    required: 'Zip code is required.',
    pattern: 'Please enter a valid postal code.',
  };
  rememberMeErrorConfig: ErrorConfig = {
    required:
      'You must agree to the Terms of Use and Privacy Policy to create an account.',
  };

  ngOnInit(): void {
    this.subscription.add(
      this.getControl('password').valueChanges.subscribe(() => {
        this.getControl('confirmPassword').updateValueAndValidity();
      }),
    );

    this.subscription.add(
      this.getControl('country').valueChanges.subscribe(
        (countryCode: string) => {
          this.selectedCountry.emit(countryCode);
          if (this.getControl('state').value) {
            this.getControl('state').setValue('');
          }
        },
      ),
    );

    this.subscription.add(
      this.getControl('state').valueChanges.subscribe((stateCode: string) => {
        this.selectedState.emit(stateCode);
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.countries?.currentValue?.length > 0) {
      this.formatCountryOptions();
    }
    if (changes?.states?.currentValue?.length > 0) {
      this.formatStatesOptions();
    }
  }

  formatCountryOptions(): void {
    this.countryOptions = this.countries.map((val: Countries) => ({
      value: val.countryCode,
      label: val.countryName,
    }));
  }

  formatStatesOptions(): void {
    this.stateOptions = this.states.map((val: States) => ({
      value: val.stateCode,
      label: val.stateName,
    }));
  }

  getControl(controlName: string): FormControl {
    return this.signUpForm.get(controlName) as FormControl;
  }

  onSignInViewClick(): void {
    this.signInViewEmitter.emit();
  }

  signUpClick(): void {
    this.signUpSubmitEmitter.emit();
  }

  onTermsClick(): void {
    this.onTermsEmitter.emit();
  }

  onPrivacyClick(): void {
    this.onPrivacyEmitter.emit();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
